import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Drawer, Flex, Form, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { useSelector } from 'react-redux';
import useApiClient from '../../hooks/api/useApiClient';
import { CompanyOperationalStatus, OperationalStatusErrors } from '../../services/exports/Constants';
import LiveOrderErrorRow from './LiveOrderErrorRow';
import './styles.css';
import useForm from '../../hooks/utility/useForm';
import YesNoRadio from '../../components/form/general/YesNoRadio';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import usePermissions from '../../hooks/auth/usePermissions';

const YesNoOnboardingQualityPoints = [
  'is_google_business_available',
  'is_smart_pricing_configured',
  'is_flyers_package_sent',
  'is_menu_fully_configured',
  'has_offers_on_competitor_platforms',
  'is_instagram_story_shared',
  'is_order_system_connected_to_website',
];

export const GoogleBusinessConnectionOptions = {
  SalesManagerOverEmail: 'sales_manager_over_email',
  SalesManagerOverPhone: 'sales_manager_over_phone',
  OnboardingManagerOverEmail: 'onboarding_manager_over_email',
};

/**
 * @param license  The paywall purchase to send with the launch, when the
 *   partner bought on the way in. Carried through the retry: the drawer only
 *   appears after a refused attempt, and dropping it here would launch the
 *   restaurant without the licence it just agreed to pay for.
 */
export default function LiveOrdersDrawer({ show = false, toggleDrawer, company, onChange, license = null }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Modals:OperationalStatus' });

  const { CompaniesManager } = useApiClient();
  const { canAccessScreen } = usePermissions();
  const { setErrorFeedback, renderFeedbackLabel, resetFeedback } = useFeedbackLabel();

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [confirmation, setConfirmation] = useState(false);
  const { data, update } = useForm({
    skipped_checks: [],
    onboarding_quality_checklist: {
      is_google_business_available: null,
      is_smart_pricing_configured: null,
      is_flyers_package_sent: null,
      is_menu_fully_configured: null,
      has_offers_on_competitor_platforms: null,
      is_instagram_story_shared: null,
      is_order_system_connected_to_website: null,
      google_business_connected_by: null,
    },
  });

  const save = async () => {
    resetFeedback();
    setLoading(true);
    const goingLive = company.operational_status !== CompanyOperationalStatus.Live;

    const response = await CompaniesManager.updateOperationalStatus({
      operational_status: goingLive ? CompanyOperationalStatus.Live : CompanyOperationalStatus.Paused,
      skipped_checks: data.skipped_checks,
      // Only on the way up: pausing never buys anything.
      ...(license && goingLive ? { license } : {}),
      onboarding_quality_checklist:
        company.operational_status !== CompanyOperationalStatus.Live &&
        serviceProvider?.is_master &&
        !company?.live_from
          ? data.onboarding_quality_checklist
          : undefined,
    });
    setLoading(false);

    if (response.status === 400) {
      const { error_code, data } = response.data;

      switch (error_code) {
        case 'operational_status_check_failed':
          return operationalStatusCheckFailedHandler(data);
        default:
          return setErrorFeedback(response.data);
      }
    }

    if (!response.success) {
      return setErrorFeedback(response.data);
    }

    onChange('operational_status', response.data.operational_status);
    toggleDrawer();
  };

  const operationalStatusCheckFailedHandler = (data) => {
    setErrors(getErrorsList.filter((item) => item.key in data));
  };

  const getErrorsList = useMemo(
    () =>
      OperationalStatusErrors.map((error) => ({
        ...error,
        // Keep the row (the check can still be skipped) but drop the link when the
        // user can't reach the target screen, e.g. Payment Gateways on a partner SP.
        url: canAccessScreen(error.url) ? error.url : null,
        title: t(`errors.${error.key}.title`),
        description: t(`errors.${error.key}.description`),
      })),
    [t, canAccessScreen],
  );

  const isOnboardingDataFilled = useMemo(
    () =>
      !serviceProvider?.is_master ||
      company?.operational_status === CompanyOperationalStatus.Live ||
      company?.live_from ||
      collect(Object.values(data.onboarding_quality_checklist)).every((item) => item !== null),
    [serviceProvider?.is_master, company?.operational_status, company?.live_from, data.onboarding_quality_checklist],
  );

  const confirmationCheckboxHandler = (e) => {
    setConfirmation(e.target.checked);
  };

  return (
    <Drawer
      title={company?.operational_status === CompanyOperationalStatus.Live ? t('titles.disable') : t('titles.enable')}
      onClose={toggleDrawer}
      open={show}
      size="large"
    >
      {renderFeedbackLabel}
      {errors.length > 0 ? (
        <>
          <div className="mb-4">{t('labels.successful_launch')}</div>
          {errors.map((error) => (
            <LiveOrderErrorRow
              error={error}
              key={error.key}
              toggle={() =>
                update(
                  'skipped_checks',
                  collect(data.skipped_checks)
                    .when(
                      data.skipped_checks.includes(error.key),
                      (collection) => collection.reject((item) => item === error.key),
                      (collection) => collection.push(error.key),
                    )
                    .toArray(),
                )
              }
            />
          ))}
          <div className="mb-4">{t('labels.check_errors')}</div>
          <Checkbox
            checked={confirmation}
            onChange={confirmationCheckboxHandler}
            className="black-border-checkbox mb-6"
          >
            {t('labels.checkbox_confirmation')}
          </Checkbox>
        </>
      ) : (
        company?.operational_status !== CompanyOperationalStatus.Live &&
        serviceProvider?.is_master &&
        !company?.live_from && (
          <Form layout="vertical">
            {YesNoOnboardingQualityPoints.map((field) => (
              <Form.Item label={t(`form.${field}.label`)} key={field}>
                <YesNoRadio
                  field={field}
                  value={data.onboarding_quality_checklist[field]}
                  onChange={(value) => update(`onboarding_quality_checklist.${field}`, value)}
                />
              </Form.Item>
            ))}
            <Form.Item label={t('form.google_business_connected_by.label')}>
              <Radio.Group
                options={Object.values(GoogleBusinessConnectionOptions).map((item) => ({
                  value: item,
                  label: t(`form.google_business_connected_by.options.${item}`),
                }))}
                onChange={(e) => update('onboarding_quality_checklist.google_business_connected_by', e.target.value)}
                className="space-y-xs"
              />
            </Form.Item>
          </Form>
        )
      )}
      <Flex gap="small" justify="flex-end" wrap>
        {errors.length > 0 ? (
          <>
            <Button
              disabled={!confirmation || data.skipped_checks.length !== errors.length}
              loading={loading}
              onClick={save}
              type="primary"
            >
              {t('buttons.confirm_and_activate')}
            </Button>
            <Button onClick={toggleDrawer} type="primary" danger>
              {t('buttons.cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button disabled={!isOnboardingDataFilled} loading={loading} onClick={save} type="primary">
              {t('buttons.yes')}
            </Button>
            <Button onClick={toggleDrawer} type="primary" danger>
              {t('buttons.no')}
            </Button>
          </>
        )}
      </Flex>
    </Drawer>
  );
}
